import type { TaskTimeLog } from '../types/project';
import {
  BUSINESS_TIME_ZONE,
  calendarDateFromApi,
  todayBusinessCalendarDate,
} from './dateTime';
import { formatDurationCompact } from './projectTaskForm';

const CLOCK = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export type TimeLogEmployeeOption = {
  employeeId: number;
  employeeName: string;
};

export type TimeLogDayGroup = {
  date: string;
  label: string;
  totalMinutes: number;
  totalLabel: string;
  entries: TaskTimeLog[];
};

export function parseClockInput(value: string): string | null {
  const match = value.trim().match(CLOCK);
  if (!match) return null;
  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
}

export function timeLogDurationMinutes(startTime: string, endTime: string): number | null {
  const start = parseClockInput(startTime);
  const end = parseClockInput(endTime);
  if (!start || !end) return null;
  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);
  const minutes = endHour * 60 + endMin - (startHour * 60 + startMin);
  if (minutes < 1 || minutes > 1440) return null;
  return minutes;
}

export const TIME_RANGE_REQUIRED_MESSAGE = 'Select both From Time and To Time.';
export const TIME_RANGE_INVALID_MESSAGE = 'Select a valid From Time and To Time.';
/** Entries stay within one day: the API stores a date plus two clock times. */
export const TIME_RANGE_ORDER_MESSAGE =
  'To Time must be later than From Time. Time after midnight must be added as a separate entry on the next day.';

/** Why a From / To pair cannot be saved, or null when it can. */
export function timeRangeError(startTime: string, endTime: string): string | null {
  if (!startTime.trim() || !endTime.trim()) return TIME_RANGE_REQUIRED_MESSAGE;
  if (!parseClockInput(startTime) || !parseClockInput(endTime)) {
    return TIME_RANGE_INVALID_MESSAGE;
  }
  return timeLogDurationMinutes(startTime, endTime) == null ? TIME_RANGE_ORDER_MESSAGE : null;
}

/** "HH:mm" (24h, business time zone) of an API date-time, or '' if unknown. */
export function clockFromApi(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  const local = text.match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (local) return `${local[1]}:${local[2]}`;
  const parsed = new Date(text);
  if (!text || Number.isNaN(parsed.getTime())) return '';
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);
  return parseClockInput(formatted) ?? '';
}

export function employeeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase() || '?';
}

export function timeLogSourceLabel(source: string): 'P' | 'D' | 'M' {
  if (source === 'POMODORO') return 'P';
  if (source === 'DEEP_FOCUS') return 'D';
  return 'M';
}

export function formatTimeLogDayLabel(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
}

export function fallbackLoggedTime(input: {
  taskId: number;
  minutes: number;
  employeeId?: number | null;
  employeeName?: string | null;
}): TaskTimeLog | null {
  if (!Number.isFinite(input.minutes) || input.minutes < 1) return null;
  const date = todayBusinessCalendarDate();
  const minutes = Math.min(Math.round(input.minutes), 1440);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ended = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  return {
    timeLogId: 0,
    taskId: input.taskId,
    employeeId: input.employeeId && input.employeeId > 0 ? input.employeeId : 0,
    employeeName: input.employeeName?.trim() || 'Logged time',
    startedAt: `${date} 00:00:00`,
    endedAt: `${date} ${ended}:00`,
    durationMinutes: minutes,
    source: 'MANUAL',
  };
}

export function logsForDisplay(
  logs: TaskTimeLog[],
  fallback: TaskTimeLog | null,
): TaskTimeLog[] {
  if (logs.length) return logs;
  return fallback ? [fallback] : [];
}

export function groupTimeLogs(logs: TaskTimeLog[]): TimeLogDayGroup[] {
  const groups = new Map<string, TaskTimeLog[]>();
  for (const log of logs) {
    const date = calendarDateFromApi(log.startedAt) ?? String(log.startedAt).slice(0, 10);
    const current = groups.get(date) ?? [];
    current.push(log);
    groups.set(date, current);
  }
  return [...groups.entries()]
    .sort((left, right) => right[0].localeCompare(left[0]))
    .map(([date, entries]) => {
      const totalMinutes = entries.reduce((sum, item) => sum + item.durationMinutes, 0);
      return {
        date,
        label: formatTimeLogDayLabel(date),
        totalMinutes,
        totalLabel: formatDurationCompact(totalMinutes) || '0m',
        entries: [...entries].sort((left, right) =>
          String(right.startedAt).localeCompare(String(left.startedAt)),
        ),
      };
    });
}

/**
 * Employees an HR Admin / Team Lead may log time for: the task's assignees,
 * plus themselves when they are a project member. The API re-validates this.
 */
export function timeLogEmployeeOptions(input: {
  assignees: readonly TimeLogEmployeeOption[];
  members: readonly TimeLogEmployeeOption[];
  selfEmployeeId?: number | null;
}): TimeLogEmployeeOption[] {
  const memberIds = new Set(input.members.map(member => member.employeeId));
  const options = input.assignees.filter(item => memberIds.has(item.employeeId));
  const self = input.members.find(member => member.employeeId === input.selfEmployeeId);
  if (self && !options.some(item => item.employeeId === self.employeeId)) {
    options.unshift(self);
  }
  return options.map(item => ({
    employeeId: item.employeeId,
    employeeName: item.employeeName,
  }));
}

export function defaultTimeLogEmployeeId(
  options: readonly TimeLogEmployeeOption[],
  selfEmployeeId?: number | null,
): number | null {
  if (options.some(item => item.employeeId === selfEmployeeId)) {
    return selfEmployeeId ?? null;
  }
  return options.length === 1 ? options[0].employeeId : null;
}

